import {useContext, useRef, useState} from "react";
import {NodeProductDocumentButtonGrid} from "@/components/organisms/widget--document-button-grid";
import {Button} from "@/components/atoms/button";
import {Link} from "@/components/atoms/link";
import VariationSelect from "@/components/organisms/widget--variation-select";
import {Heading} from "@/components/atoms/heading";
import ProductImages from "@/components/organisms/widget--product-images";
import {Dictionary, DrupalProductNode, DrupalProductVariationGroup} from "@/types/hygiena-types";
import {getProductVariations} from "@/lib/get-product-variations";
import classNames from "classnames";
import parse from "html-react-parser";
import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";
import {getDocumentUri} from "@/lib/get-document-uri";
import RenderProductNumber from "@/components/molecules/product-numbers";

const FormattedText = dynamic(() => import("../../formatted-text").then((mod) => mod.FormattedText));
/**
 * A styled container element to hold a subproduct.
 *
 * @param children
 * @constructor
 */
export function NodeSubProductContainer ({children}: {children?: any}) {
  return (
    <div className="sub-product-container bg-white drop-shadow-sm px-8 md:px-16 pt-12 pb-3 flex flex-col gap-16 rounded-md">
      {children}
    </div>
  )
}

/**
 * A sub-product display used when showing products like additional, reagents, equipment etc.
 *
 * @param node
 *   The product node.
 * @param openModal
 *   The open modal callback.
 * @param openModalContent
 *   The open modal content callback.
 * @constructor
 */
export function NodeSubProduct(
  {
    node,
    openModal,
    openModalContent,
    headerType,
  }: {
    node: DrupalProductNode,
    openModal?: Function|null,
    openModalContent?: Function|null,
    headerType?: 1|2|3|4|5|6,
  }) {
  const t = useContext<Dictionary>(DictionaryContext);

  const variations = getProductVariations(node);
  const [activeVariant, setActiveVariant] = useState<DrupalProductVariationGroup|null>(variations?.[0] ?? null);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const certificates = node?.field_product_certificates?.length
    ? node.field_product_certificates.filter(item => item?.field_certificate_type?.name !== undefined)
    : [];
  const certsNoFile = node?.field_product_certs_no_file?.filter(certificate => certificate?.name?.length) ?? [];

  const buttonExpandedCallback = (val: boolean) => {
    if (!ref?.current) return;
    const parent = ref.current?.parentElement;
    if (parent && val) {
      parent.style["z-index"] = "10";
      parent.style["position"] = "relative";
    }
    else if (parent) {
      parent.style["z-index"] = "";
      parent.style["position"] = "";
    }
  }

  return (
    <>
      <div className={"flex flex-col md:grid md:grid-cols-2 text-black"} ref={ref}>
        <div className="mb-4 flex justify-center items-center">
          <ProductImages image_field={node?.field_product_image ?? []} video_field={node?.field_product_video} openModal={openModal} openModalContent={openModalContent} videoRef={videoRef} lazy={true}/>
        </div>
        <div>
          <div className="pb-6 text-3xl">
            <Heading headerType={headerType ?? 3} className={classNames("font-bold leading-tight max-md:break-words", {"text-3xl": headerType === 4})}>
              {node?.field_product_brand && (
                <>
                  <span className="max-md:block">{node.field_product_brand?.name}{node.field_product_brand?.field_brand_suffix && node.field_product_brand.field_brand_suffix}&nbsp;</span>
                </>
              )}
              {node?.field_product_display_title?.value ? parse(node?.field_product_display_title.value, {}) : node.title}
            </Heading>
          </div>
          <div>
            <div className="font-light mb-4 max-md:leading-body-lg">
              {node.body?.processed && <FormattedText processed={node.body.processed}/>}
            </div>
            {node?.field_product_test_targets?.length ? (
              <div className="font-light mb-3">
                <label className="mr-1">{t?.all?.detection}:</label>
                {node?.field_product_test_targets && node.field_product_test_targets.map((item, key) => (
                  <span key={key}>{item.name}{key !== node.field_product_test_targets.length - 1 && ", "}</span>
                ))}
              </div>
            ): <></>}
            {!node?.field_product_class && (certificates?.length || certsNoFile?.length) ? (
              <div className="font-light mb-3">
                <label className="mr-1"><>{certificates?.length > 1 ? `${t?.all?.certificates}` : `${t?.all?.certificate}`}:</></label>
                {certificates?.length ? certificates.map((item, i) => (
                  <Link key={`node-product-teaser--certificates--${item.id}-${i}`} target={"_blank"} href={getDocumentUri(item) ?? "/"} className="text-primary hover:underline">
                    {item?.field_certificate_type?.name}
                    {(i !== certificates.length - 1 || certsNoFile?.length) ? ", " : ""}
                  </Link>
                )): ""}
                {certsNoFile?.length ? certsNoFile.map((item, i) => (
                  <span key={`certificates-with-no-file--${item.name}-${i}`} className="text-black">
                    {item?.name} {item?.refNo?.length ? `(${item.refNo})` : ''}
                    {i !== certsNoFile.length - 1 && ", "}
                  </span>
                )) : ""}
                {(!certificates?.length && !certsNoFile?.length) ? "-" : ""}
              </div>
            ) : <></>}
            <div className="font-light mb-3">
              <VariationSelect variations={variations} callback={setActiveVariant} hoverEffect={true} chevronColor="black" className="!text-black !font-normal" />
            </div>
            {activeVariant && (
              <div className="font-regular md:mb-8">
                <RenderProductNumber variant={activeVariant} showModal={false} />
              </div>
            )}
            <div className="mb-3 max-md:hidden">
              <NodeProductDocumentButtonGrid node={node} videoRef={videoRef} hoverCallback={buttonExpandedCallback} viewMode="subproduct"/>
            </div>
            {node?.path?.alias && (
              <>
                <div className="mb-3 text-right md:hidden">
                  <Button href={node.path.alias} className="py-1.5 px-6 btn-product-details" hover={{color: "primary"}} invert={true} containerClassName={"!w-full !block"}>{`${t?.all?.see_product_details}`}</Button>
                </div>
                <div className="mb-3 text-right max-md:hidden">
                  <Link href={node?.path?.alias ?? "/"} className="text-primary inline pl-3"><>{t?.all?.go_to_product_page} <span>&#8594;</span></></Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

